import React from "react";
import { C } from "../theme";
import useTidePredictions from "../hooks/useTidePredictions";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDayHeader(date) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const isTomorrow =
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate();

  if (isToday) return `Today · ${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;
  if (isTomorrow) return `Tomorrow · ${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;
  return `${DAY_LABELS[date.getDay()]} · ${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
}

function groupByDay(predictions) {
  const groups = {};
  for (const p of predictions) {
    const key = `${p.time.getFullYear()}-${p.time.getMonth()}-${p.time.getDate()}`;
    if (!groups[key]) groups[key] = { date: p.time, tides: [] };
    groups[key].tides.push(p);
  }
  return Object.values(groups).sort((a, b) => a.date - b.date);
}

export default function TideForecastModal({ stationId, stationName, onClose }) {
  const { predictions, loading, error } = useTidePredictions({
    stationId,
    enabled: !!stationId,
  });

  const groups = groupByDay(predictions);

  return (
    // Backdrop
    <div
      onClick={onClose}
      style={{
        // `absolute` (not `fixed`) so the sheet stays inside the app's centered
        // "phone frame" (position:relative, overflow:hidden) on wider screens /
        // tablets. With `fixed` it anchored to the full viewport and rendered
        // the bottom sheet outside the frame boundary.
        position: "absolute",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(0,0,0,0.55)",
        padding: "0 10px calc(env(safe-area-inset-bottom, 0px) + 8px)",
        boxSizing: "border-box",
      }}
    >
      {/* Sheet — stop propagation so clicks inside don't close */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(100%, 375px)",
          height: "min(42dvh, 360px)",
          maxHeight: "calc(100dvh - env(safe-area-inset-bottom, 0px) - 24px)",
          minHeight: 220,
          background: C.deepWater,
          borderRadius: 18,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 16px 10px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>
              TIDE FORECAST · 3 DAYS
            </div>
            <div style={{ fontSize: 15, color: C.white, fontWeight: 700 }}>
              {stationName || "Tide Station"}
            </div>
            <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>Source: NOAA CO-OPS</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
              padding: "0 2px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          className="hide-scrollbar"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "10px 14px 24px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {loading && (
            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", paddingTop: 24 }}>
              Loading tide predictions…
            </div>
          )}

          {!loading && error && (
            <div style={{ fontSize: 12, color: C.warning, textAlign: "center", paddingTop: 24 }}>
              {error}
            </div>
          )}

          {!loading && !error && groups.length === 0 && (
            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", paddingTop: 24 }}>
              No predictions available for this station.
            </div>
          )}

          {!loading && !error && groups.map((group) => (
            <div key={group.date.toDateString()} style={{ marginBottom: 14 }}>
              {/* Day header */}
              <div
                style={{
                  fontSize: 10,
                  color: C.teal,
                  letterSpacing: 2,
                  marginBottom: 6,
                  paddingBottom: 4,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {formatDayHeader(group.date)}
              </div>

              {/* Tide rows */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {group.tides.map((tide, i) => {
                  const isHigh = tide.type === "H";
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "9px 12px",
                        borderRadius: 10,
                        background: C.cardBg,
                        border: `1px solid ${isHigh ? C.accent + "44" : C.border}`,
                      }}
                    >
                      {/* Badge + label */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: isHigh ? C.accent + "22" : C.midnight,
                            border: `1.5px solid ${isHigh ? C.accent : C.muted + "66"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontSize: 14 }}>{isHigh ? "▲" : "▼"}</span>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: C.white, fontWeight: 700 }}>
                            {isHigh ? "High Tide" : "Low Tide"}
                          </div>
                          <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>
                            {formatTime(tide.time)}
                          </div>
                        </div>
                      </div>

                      {/* Height */}
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: isHigh ? C.accent : C.muted,
                          }}
                        >
                          {tide.heightFt.toFixed(1)}
                          <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 2 }}>ft</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
